import { useEffect, useRef, useState } from "react";
import { Card, Row, Col, Button, Modal, Dropdown } from "react-bootstrap";
import { useAppSelector } from "../../../redux/hooks";
import { lddStateObject } from "../../../redux/reducer";
import { RPWDataReader, getDataReader } from "../../../livedataFuncs";
import { useEsignAuth } from "../../Esign/useEsignAuth";
import { getCachedSubForSid } from "../../Esign/oidcManager";
import { useEsignAllowlist } from "./useEsignAllowlist";
import { resolveLawyerEmail } from "./esignLawyerEmail";
import { dateToXMLDate } from "../../../util/genericDateAndTimeFuncs";

//   REACT_APP_ESIGN_APP_BASE_URL=http://localhost:5179
//   REACT_APP_ESIGN_API_BASE_URL=http://localhost:3301
const ESIGN_APP_BASE_URL = process.env.REACT_APP_ESIGN_APP_BASE_URL ?? '/esignportal';
const ESIGN_API_BASE_URL = process.env.REACT_APP_ESIGN_API_BASE_URL ?? '/esignportal';

/** Bearer header helper — produces an empty headers object when jwt is null. */
const bearerHeaders = (jwt: string | null): Record<string, string> =>
    jwt ? { Authorization: `Bearer ${jwt}` } : {};

const formatDateTimeYMD = (value: string | number | Date): string => {
    const d = new Date(value);
    return `${dateToXMLDate(d)} ${d.toLocaleTimeString()}`;
};

/**
 * Compose the esign SPA URL for a given page. Appends `?account=<sub>` when
 * the active esign sub is known so the SPA lands directly in the right
 * per-user partition (no AccountPicker bounce).
 */
function withAccount(path: string, sid: string): string {
    const sub = getCachedSubForSid(sid);
    if (!sub) return `${ESIGN_APP_BASE_URL}${path}`;
    const sep = path.includes('?') ? '&' : '?';
    return `${ESIGN_APP_BASE_URL}${path}${sep}account=${encodeURIComponent(sub)}`;
}

const flipName = (name: string): string => {
    if (!name) return '';
    const parts = name.split(',').map(p => p.trim());
    if (parts.length === 2) return `${parts[1]} ${parts[0]}`;
    return name;
};

// Split a name into first/middle/last parts. Source XML is typically
// "Last, First [Middle]" (LDD convention). Also handles the alternate
// "First [Middle] Last" order — split on last whitespace when no comma.
// Required by MB (jurats), optional for other provinces.
const parseNameParts = (rawName: string): { firstName: string; middleName: string; lastName: string } => {
    if (!rawName) return { firstName: '', middleName: '', lastName: '' };
    const trimmed = rawName.trim();
    const commaIdx = trimmed.indexOf(',');
    if (commaIdx >= 0) {
        const lastName = trimmed.slice(0, commaIdx).trim();
        const rest = trimmed.slice(commaIdx + 1).trim().split(/\s+/).filter(Boolean);
        const firstName = rest[0] ?? '';
        const middleName = rest.slice(1).join(' ');
        return { firstName, middleName, lastName };
    }
    const parts = trimmed.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { firstName: '', middleName: '', lastName: '' };
    if (parts.length === 1) return { firstName: parts[0], middleName: '', lastName: '' };
    return {
        firstName: parts[0],
        middleName: parts.slice(1, -1).join(' '),
        lastName: parts[parts.length - 1],
    };
};

// Province-aware display composition. MB/SK/BC show names as
// "Last, First Middle" (unflipped source); ON/AB (and other provinces
// pending confirmation) show "First Last".
const COMMA_ORDER_PROVINCES = new Set(['MB', 'SK', 'BC']);
const composeDisplayName = (rawName: string, province: string): string => {
    if (!rawName) return '';
    return COMMA_ORDER_PROVINCES.has(province) ? rawName.trim() : flipName(rawName);
};

interface MyDriveCtx {
    sessionId: string;
    appCode: string;
    userId: string;
}

// Mirrors replaceMatterNumberSpecialChar in FileDrive/filedriveFuncs.ts — the
// SharePoint folder name has special chars replaced with `-`, so we need to
// apply the same transform to the raw matter number before composing URLs.
const MATTER_SPECIAL_CHARS = ['|', ':', '?', '*', '%', '#', '\\', '/', '<', '>', '.', '{', '}', '"', '`'];
const normalizeMatterFolderName = (matterNumber: string): string => {
    let out = matterNumber || '';
    for (const ch of MATTER_SPECIAL_CHARS) out = out.split(ch).join('-');
    return out.trim();
};

const buildInitialData = (dataReader: RPWDataReader, mydriveCtx?: MyDriveCtx) => {
    const rootAttrs = (dataReader.getTPAppXMLNode?.()[0] as any)?.attributes ?? {};
    const lawyerNodes: any[] = dataReader.getNodeInformation?.('lawyer') ?? [];
    const lawyerNode = lawyerNodes[0];
    const lawyerAttrs = lawyerNode?.attributes ?? {};

    const lawyerEmail = resolveLawyerEmail(lawyerNode);

    // Firm contact email: ContactInfo child of the lawyer node with
    // ContactMethod='Email' carries the firm's email in ContactMethodInfo.
    const lawyerChildren: any[] = lawyerNode?.children ?? [];
    const firmEmailContact = lawyerChildren.find((c) => c?.name === 'ContactInfo' && c?.attributes?.ContactMethod === 'Email');
    const firmEmail = firmEmailContact?.attributes?.ContactMethodInfo ?? '';
    const firmName = lawyerAttrs.FirmName ?? '';
    const responsibleLawyer = flipName(lawyerAttrs.LawyerName ?? lawyerAttrs.Name ?? '');

    const psmMap: Record<string, string> = { P: 'Purchase', S: 'Sale', M: 'Mortgage' };
    const fileType = psmMap[rootAttrs.PSM] ?? rootAttrs.PSM ?? '';

    const province = (rootAttrs.ApplicationSubCode ?? 'ON').toUpperCase();

    const lawyerRawName = lawyerAttrs.Name ?? '';
    const lawyerParts = parseNameParts(lawyerRawName);
    const participants: any[] = [
        {
            name: composeDisplayName(lawyerRawName, province),
            firstName: lawyerParts.firstName,
            middleName: lawyerParts.middleName,
            lastName: lawyerParts.lastName,
            email: lawyerEmail,
            role: 'Lawyer',
            isSupervisor: true,
            isEditable: false,
        },
    ];

    const psm = dataReader.getPSM?.();
    const clientNodeName = psm === 'S' ? 'Vendor' : 'Purchaser';
    const clientNodes: any[] = dataReader.getNodeInformation?.(clientNodeName) ?? [];

    // Track emails across both extraction paths so a person listed under
    // both flat Purchaser/Vendor and APSNames[@Client='1'] (or on multiple
    // APSNames types) doesn't come through twice.
    const seenEmails = new Set<string>();
    const noteEmail = (email: string) => {
        if (email) seenEmails.add(email.toLowerCase());
    };

    clientNodes.forEach((node: any) => {
        const rawName = node?.attributes?.Name ?? '';
        if (!rawName.trim()) return;
        const displayed = composeDisplayName(rawName, province);
        const entityType = node?.attributes?.EntityType ?? '';
        const gender = node?.attributes?.Gender ?? '';
        const isEstateAttr = node?.attributes?.IsEstate === '1';
        const isEstate = entityType === 'Estate' || isEstateAttr || /^the estate of\b/i.test(displayed);
        const isCorporation = entityType === 'Corporation' || gender === 'Corporation';
        const children: any[] = node?.children ?? [];
        const emailContact = children.find((c) => c?.name === 'ContactInfo' && c?.attributes?.ContactMethod === 'Email');
        const email = emailContact?.attributes?.ContactMethodInfo ?? '';

        if (isEstate) {
            const signingAuthorityType = node?.attributes?.SigningAuthorityRole ?? '';
            const signerRawNames: string[] = [];
            const primaryPOA = node?.attributes?.POAName ?? '';
            if (primaryPOA.trim()) signerRawNames.push(primaryPOA);
            children.forEach((c) => {
                if (c?.name === 'ExtraPOAName') {
                    const extraName = c?.attributes?.POAName ?? '';
                    if (extraName.trim()) signerRawNames.push(extraName);
                }
            });
            const list = signerRawNames.length === 0 ? [''] : signerRawNames;
            list.forEach((signerRaw) => {
                const signerParts = parseNameParts(signerRaw);
                participants.push({
                    name: displayed, email, role: 'Signer',
                    isSupervisor: false, isEditable: false, isEstate: true,
                    signingAuthorityType,
                    signerName: composeDisplayName(signerRaw, province),
                    signerFirstName: signerParts.firstName,
                    signerMiddleName: signerParts.middleName,
                    signerLastName: signerParts.lastName,
                    signerEmail: '',
                });
            });
        } else if (isCorporation) {
            const officerEntries = children
                .filter((c) => c?.name === 'SignOff')
                .map((officer) => ({
                    rawName: officer?.attributes?.SignOffName ?? '',
                    title: officer?.attributes?.SignOffTitle ?? '',
                }))
                .filter((o) => o.rawName.trim() !== '');
            const list = officerEntries.length === 0 ? [{ rawName: '', title: '' }] : officerEntries;
            list.forEach((officer) => {
                const officerParts = parseNameParts(officer.rawName);
                participants.push({
                    name: displayed, email, role: 'Signer',
                    isSupervisor: false, isEditable: false, isCorporation: true,
                    signingAuthorityType: officer.title,
                    signerName: composeDisplayName(officer.rawName, province),
                    signerFirstName: officerParts.firstName,
                    signerMiddleName: officerParts.middleName,
                    signerLastName: officerParts.lastName,
                    signerEmail: '',
                });
            });
        } else {
            const parts = parseNameParts(rawName);
            participants.push({
                name: displayed,
                firstName: parts.firstName,
                middleName: parts.middleName,
                lastName: parts.lastName,
                email, role: 'Signer',
                isSupervisor: false, isEditable: false, isEstate: false,
            });
            noteEmail(email);
        }
    });

    // MB (and any other province that uses the APSNames shape) stores
    // clients as one <Name> per person under <APSNames Client="1">, with
    // structured OnTitle{First,Middle,Last}Name attributes and a
    // <ContactInfo ContactMethod="Email"> child. Extract these alongside the
    // flat Purchaser/Vendor path — dedupe by email so a person carried on
    // both shapes doesn't emit twice.
    const apsGroups: any[] = (dataReader.getNodeInformation?.('APSNames') ?? [])
        .filter((n: any) => n?.attributes?.Client === '1');
    apsGroups.forEach((group: any) => {
        const nameChildren: any[] = (group?.children ?? []).filter((c: any) => c?.name === 'Name');
        nameChildren.forEach((nameNode: any) => {
            const attrs = nameNode?.attributes ?? {};
            const first = attrs.OnTitleFirstName ?? '';
            const middle = attrs.OnTitleMiddleName ?? '';
            const last = attrs.OnTitleLastName ?? '';
            const composedRaw = `${last}, ${first}${middle ? ' ' + middle : ''}`.trim();
            if (!composedRaw || composedRaw === ',') return;

            const emailContact = (nameNode?.children ?? [])
                .find((c: any) => c?.name === 'ContactInfo' && c?.attributes?.ContactMethod === 'Email');
            const email = emailContact?.attributes?.ContactMethodInfo ?? '';
            if (email && seenEmails.has(email.toLowerCase())) return;

            const entityType = attrs.EntityType ?? '';
            const gender = attrs.Gender ?? '';
            const isEstate = entityType === 'Estate' || attrs.IsEstate === '1';
            const isCorporation = entityType === 'Corporation' || gender === 'Corporation';

            if (isEstate || isCorporation) {
                // Fall through to the existing entity shape. Signer parts are
                // sourced from the same OnTitle* attributes, since APSNames
                // Names are the entity's signer identity in this shape.
                participants.push({
                    name: composeDisplayName(composedRaw, province),
                    email, role: 'Signer',
                    isSupervisor: false, isEditable: false,
                    ...(isEstate ? { isEstate: true } : { isCorporation: true }),
                    signingAuthorityType: attrs.SigningAuthorityRole ?? '',
                    signerName: composeDisplayName(composedRaw, province),
                    signerFirstName: first,
                    signerMiddleName: middle,
                    signerLastName: last,
                    signerEmail: email,
                });
            } else {
                participants.push({
                    name: composeDisplayName(composedRaw, province),
                    firstName: first,
                    middleName: middle,
                    lastName: last,
                    email, role: 'Signer',
                    isSupervisor: false, isEditable: false, isEstate: false,
                });
            }
            noteEmail(email);
        });
    });

    const matterNumber = rootAttrs.MatterNumber ?? '';
    const appId = (dataReader.getAppId?.() ?? '').toString();
    const rwApi = process.env.REACT_APP_RWAPI_ENDPOINT ?? '';

    let mydrive: any = null;
    if (mydriveCtx?.sessionId && mydriveCtx?.appCode && mydriveCtx?.userId && appId) {
        const proxyUrl = `${window.location.origin}/${rwApi}/api/${mydriveCtx.appCode}/${mydriveCtx.userId}/${appId}/filedrive/sendToSharepoint?SessionID=${encodeURIComponent(mydriveCtx.sessionId)}`;
        mydrive = {
            proxyUrl,
            rootDrive: 'ldd_fake_drive_root',
            host: 'ldd_fake_drive_tenant',
            matterNumber: normalizeMatterFolderName(matterNumber),
        };
    }

    // Property address — eSign uses this in client-facing email subjects
    // and headings so signers identify the matter by the property they're
    // transacting on. Prefer the dataReader accessor (handles the per-reader
    // XPath variance); fall back to the root attribute on TPAppXML.
    const propertyAddress = (dataReader.getPropertyAddress?.() ?? rootAttrs.PropertyAddress ?? '').toString();

    return {
        // Legacy X-API-Key field. The esign SPA's API client now authenticates
        // via the OIDC partition resolved from `?account=<sub>` on the iframe
        // URL, so this value is ignored on the wire. Kept so the iframe's
        // destructuring code doesn't see `undefined`.
        apiKey: rootAttrs.LawyerID ?? '',
        applicationSubCode: rootAttrs.ApplicationSubCode ?? 'ON',
        fileInfo: {
            fileType,
            matterNumber,
            reLine: rootAttrs.ReLine ?? '',
            propertyAddress,
            // Province code the eSign service uses to pick province-specific
            // signing rules (jurat templates, notary regs, etc.). Same source
            // as the top-level applicationSubCode; duplicated inside fileInfo
            // to match the eSign realtiImport contract.
            province: rootAttrs.ApplicationSubCode ?? 'ON',
            // BC-specific: firm is a notary (rather than lawyer) office.
            // Drives jurat wording, wet-ink toggle, and Notary/Lawyer labels
            // on the eSign side. Absent / non-"1" defaults to false (lawyer).
            isNotaryFirm: rootAttrs.isNotaryFirm === '1',
            jurat: {
                city: lawyerAttrs.FirmCitySign ?? '',
                municipality: lawyerAttrs.FirmMunicipalitySign ?? '',
            },
            firmContact: {
                staffName: rootAttrs.StaffName ?? '',
                firmEmail,
                firmName,
                responsibleLawyer,
                // Responsible lawyer's own email + lawyer/notary classification,
                // pulled from the file XML (see resolveLawyerEmail for the
                // source priority, and isNotaryFirm for the type). The
                // eSign service writes these into the signing_tasks
                // lawyer_email / lawyer_type columns at task-creation time.
                responsibleLawyerEmail: lawyerEmail,
                responsibleLawyerType: lawyerAttrs.isNotaryFirm === '1' ? 'notary' : 'lawyer',
            },
        },
        participants,
        mydrive,
    };
};

async function handleMyDriveRequest(req: any, target: Window | null) {
    if (!target) return;
    const respond = (payload: any) => target.postMessage({ type: 'mydriveResponse', requestId: req.requestId, ...payload }, '*');
    try {
        if (req.kind === 'list') {
            const midPackage = {
                type: 'application/json',
                rootDrive: req.rootDrive,
                url: req.url,
                method: req.method ?? 'GET',
                postData: req.postData,
                options: undefined,
            };
            console.log('[mydrive] list request', { proxyUrl: req.proxyUrl, midPackage });
            const res = await fetch(req.proxyUrl, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(midPackage),
            });
            if (!res.ok) {
                const body = await res.text().catch(() => '');
                console.warn('[mydrive] list failed', res.status, body);
                respond({ ok: false, error: `MyDrive list failed: ${res.status} ${body.slice(0, 300)}` });
                return;
            }
            const data = await res.json();
            if (data?.error) {
                respond({ ok: false, error: data.error });
                return;
            }
            respond({ ok: true, data });
        } else if (req.kind === 'blob') {
            const blobProxyUrl = req.proxyUrl.replace('sendToSharepoint', 'getBlob') + `&lddurl=${encodeURIComponent(req.spUrl)}`;
            const res = await fetch(blobProxyUrl, { method: 'GET', credentials: 'include' });
            if (!res.ok) {
                const body = await res.text().catch(() => '');
                console.warn('[mydrive] blob failed', res.status, body);
                respond({ ok: false, error: `MyDrive blob failed: ${res.status} ${body.slice(0, 300)}` });
                return;
            }
            const arrayBuffer = await res.arrayBuffer();
            target.postMessage({
                type: 'mydriveResponse',
                requestId: req.requestId,
                ok: true,
                arrayBuffer,
                contentType: res.headers.get('content-type') ?? 'application/octet-stream',
            }, '*', [arrayBuffer]);
        } else if (req.kind === 'linkBlob') {
            // LDD link file flow:
            //   1. Get the link file's text content (use the @microsoft.graph.downloadUrl from
            //      the listing if present, otherwise fall back to the proxy /items/{id}/content
            //      endpoint, finally fall back to a `?comp=metadata` round-trip).
            //   2. Hit storageDocGet with that text as `fullPath` + the display `nameRequest`.
            let linkText: string | null = null;

            // Path A: direct downloadUrl from the listing entry.
            if (req.downloadUrl) {
                try {
                    const linkRes = await fetch(req.downloadUrl, { method: 'GET' });
                    if (linkRes.ok) linkText = await linkRes.text();
                    else console.warn('[mydrive] direct downloadUrl failed', linkRes.status);
                } catch (e) { console.warn('[mydrive] direct downloadUrl threw', e); }
            }

            // Path B: items endpoint via getBlob proxy using item id from listing.
            if (!linkText && req.itemId) {
                const hostPrefix = req.spUrl.split('/root:')[0];
                const itemsUrl = `${hostPrefix}/items/${req.itemId}/content`;
                const fallbackUrl = req.proxyUrl.replace('sendToSharepoint', 'getBlob') + `&lddurl=${encodeURIComponent(itemsUrl)}`;
                try {
                    const r = await fetch(fallbackUrl, { method: 'GET', credentials: 'include' });
                    if (r.ok) linkText = await r.text();
                    else console.warn('[mydrive] items getBlob failed', r.status, await r.text().catch(() => ''));
                } catch (e) { console.warn('[mydrive] items getBlob threw', e); }
            }

            // Path C: metadata round-trip to find downloadUrl.
            if (!linkText) {
                const metaRes = await fetch(req.proxyUrl, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        type: 'application/json',
                        rootDrive: req.rootDrive,
                        url: `${req.spUrl}?comp=metadata`,
                        method: 'GET',
                        postData: undefined,
                        options: undefined,
                    }),
                });
                const metaData = metaRes.ok ? await metaRes.json().catch(() => null) : null;
                console.log('[mydrive] link metadata fallback', metaData);
                const dl = metaData?.['@microsoft.graph.downloadUrl'];
                if (dl) {
                    const linkRes = await fetch(dl, { method: 'GET' });
                    if (linkRes.ok) linkText = await linkRes.text();
                }
            }

            if (!linkText) {
                respond({ ok: false, error: 'Could not retrieve link file content (all paths failed). Open MyDrive directly to confirm the file works there.' });
                return;
            }
            console.log('[mydrive] link file text', { length: linkText.length, preview: linkText.slice(0, 200) });
            const storageUrl = req.proxyUrl.replace('sendToSharepoint', 'storageDocGet')
                + `&fullPath=${encodeURIComponent(linkText)}`
                + `&nameRequest=${encodeURIComponent(req.fileName ?? '')}`;
            const fileRes = await fetch(storageUrl, { method: 'GET', credentials: 'include' });
            if (!fileRes.ok) {
                const body = await fileRes.text().catch(() => '');
                respond({ ok: false, error: `Link file resolve failed: ${fileRes.status} ${body.slice(0, 300)}` });
                return;
            }
            const arrayBuffer = await fileRes.arrayBuffer();
            target.postMessage({
                type: 'mydriveResponse',
                requestId: req.requestId,
                ok: true,
                arrayBuffer,
                contentType: fileRes.headers.get('content-type') ?? 'application/octet-stream',
            }, '*', [arrayBuffer]);
        } else {
            respond({ ok: false, error: `Unknown mydrive kind: ${req.kind}` });
        }
    } catch (e: any) {
        respond({ ok: false, error: e?.message ?? 'MyDrive request failed' });
    }
}

const ESigningCard = () => {
    const [showModal, setShowModal] = useState<boolean>(false);
    const [fullscreen, setFullscreen] = useState<boolean>(false);
    const [sessions, setSessions] = useState<any[]>([]);
    const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
    const [rescheduling, setRescheduling] = useState<boolean>(false);
    const [finishSetupUrl, setFinishSetupUrl] = useState<string | null>(null);
    const [completedTaskDetails, setCompletedTaskDetails] = useState<any | null>(null);
    const [loadingCompletedDetails, setLoadingCompletedDetails] = useState<boolean>(false);
    const [showEmailLog, setShowEmailLog] = useState<boolean>(false);
    const [emailEvents, setEmailEvents] = useState<any[]>([]);
    const [loadingEmailLog, setLoadingEmailLog] = useState<boolean>(false);
    const [emailLogError, setEmailLogError] = useState<string>('');
    const [collapsed, setCollapsed] = useState<boolean>(false);
    const [refreshing, setRefreshing] = useState<boolean>(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const livedataRootContainer = useAppSelector((state: lddStateObject) => state.currentNavigation.livedataRootContainer);
    const activeTabName = useAppSelector((state: any) => state?.currentNavigation?.page?.controller?.tabName);
    const sessionInfo = useAppSelector((state: any) => state.SessionInfo);
    const { allowed, loading: allowlistLoading } = useEsignAllowlist();
    const dataReader = getDataReader(livedataRootContainer.dataRoot);
    const sid: string = sessionInfo?.sid ?? '';
    // Bootstraps the OIDC trusted-token grant on first render and keeps the
    // returned access token fresh. Until `jwt` resolves, fetches are skipped
    // (rather than firing a guaranteed 401). Also gated on `allowed` so firms
    // not on the eSigning allowlist don't trigger the OIDC bootstrap at all.
    const { jwt } = useEsignAuth(sid, /* enabled */ Boolean(sid) && allowed);
    const mydriveCtx: MyDriveCtx = {
        sessionId: sid,
        appCode: sessionInfo?.appCode ?? '',
        userId: (sessionInfo?.firm?.id ?? '').toString(),
    };

    const fetchSessions = async () => {
        const rootAttrs = (dataReader.getTPAppXMLNode?.()[0] as any)?.attributes ?? {};
        const matterNumber = (rootAttrs.MatterNumber ?? '').toString().trim();
        console.log('[eSign] fetchSessions called', { hasJwt: !!jwt, matterNumber, jwtPreview: jwt ? `${jwt.slice(0, 12)}…` : null });
        if (!jwt || !matterNumber) {
            console.log('[eSign] fetchSessions skipped — missing', !jwt ? 'jwt' : 'matterNumber');
            return;
        }
        try {
            const url = `${ESIGN_API_BASE_URL}/api/tasks?context_ref=${encodeURIComponent(matterNumber)}`;
            const res = await fetch(url, { headers: bearerHeaders(jwt) });
            if (!res.ok) {
                const text = await res.text().catch(() => '');
                console.warn('[eSign] fetch failed', res.status, text);
                return;
            }
            const data = await res.json();
            const list = Array.isArray(data) ? data : (data?.tasks ?? []);
            console.log('[eSign] fetchSessions success — got', list.length, 'sessions for matter', matterNumber);
            setSessions(list);
        } catch (e) {
            console.warn('[eSign] Failed to fetch eSign sessions', e);
        }
    };

    const openFinishSetup = async (taskId: string) => {
        if (!jwt) return;
        try {
            const res = await fetch(`${ESIGN_API_BASE_URL}/api/tasks/${encodeURIComponent(taskId)}`, {
                headers: bearerHeaders(jwt),
            });
            if (!res.ok) {
                console.warn('[eSign] finish-setup: failed to load task', res.status);
                return;
            }
            const task = await res.json();
            const firstDocId = task?.documents?.[0]?.id;
            const url = firstDocId
                ? withAccount(`/tasks/${encodeURIComponent(taskId)}/documents/${encodeURIComponent(firstDocId)}/review-detections?source=cc`, sid)
                : withAccount(`/tasks/${encodeURIComponent(taskId)}/setup?source=cc`, sid);
            setFinishSetupUrl(url);
            setShowModal(true);
        } catch (e) {
            console.warn('[eSign] finish-setup error', e);
        }
    };

    const openEmailLog = async () => {
        if (!jwt) return;
        setShowEmailLog(true);
        setLoadingEmailLog(true);
        setEmailLogError('');
        setEmailEvents([]);
        try {
            const rootAttrs = (dataReader.getTPAppXMLNode?.()[0] as any)?.attributes ?? {};
            const matterNumber = (rootAttrs.MatterNumber ?? '').toString().trim();
            if (!matterNumber) {
                setEmailEvents([]);
                return;
            }
            // Matter-level endpoint — aggregates across every session for the
            // context_ref, including soft-deleted ones. Lets the card surface
            // emails from sessions that were deleted (and so don't appear in
            // the visible list) without enumerating their task_ids.
            const res = await fetch(
                `${ESIGN_API_BASE_URL}/api/postmark/events?context_ref=${encodeURIComponent(matterNumber)}&limit=500`,
                { headers: bearerHeaders(jwt) },
            );
            if (!res.ok) {
                throw new Error(`${res.status}`);
            }
            const events = await res.json();
            setEmailEvents(Array.isArray(events) ? events : []);
        } catch (e: any) {
            setEmailLogError(e?.message || 'Failed to load email log');
        } finally {
            setLoadingEmailLog(false);
        }
    };

    const openCompletedDetails = async (taskId: string) => {
        if (!jwt) return;
        setLoadingCompletedDetails(true);
        setCompletedTaskDetails({ taskId, loading: true });
        try {
            const res = await fetch(`${ESIGN_API_BASE_URL}/api/tasks/${encodeURIComponent(taskId)}/results`, {
                headers: bearerHeaders(jwt),
            });
            if (!res.ok) {
                const body = await res.text().catch(() => '');
                setCompletedTaskDetails({ taskId, error: `Failed to load: ${res.status} ${body.slice(0, 200)}` });
                return;
            }
            const data = await res.json();
            setCompletedTaskDetails({ taskId, data });
        } catch (e: any) {
            setCompletedTaskDetails({ taskId, error: e?.message || 'Failed to load' });
        } finally {
            setLoadingCompletedDetails(false);
        }
    };

    const deleteSession = async (taskId: string) => {
        if (!jwt) return;
        if (!window.confirm('Delete this eSigning session?')) return;
        try {
            const res = await fetch(`${ESIGN_API_BASE_URL}/api/tasks/${encodeURIComponent(taskId)}/delete`, {
                method: 'POST',
                headers: bearerHeaders(jwt),
            });
            if (!res.ok) {
                console.warn('[eSign] delete failed', res.status);
                return;
            }
            // Refetch so the row reappears in the deleted subgroup rather than
            // disappearing — keeps the card-level Email Log able to surface
            // the deleted session's email history.
            await fetchSessions();
        } catch (e) {
            console.warn('[eSign] delete error', e);
        }
    };

    // fetchSessions needs both `jwt` (set by useEsignAuth after the OIDC
    // bootstrap finishes) AND `matterNumber` (extracted from the file XML
    // which loads asynchronously). Either can arrive in either order, so we
    // re-run whenever either changes. Reading matterNumber from the data
    // reader inline so the effect re-runs when the underlying XML appears.
    const matterNumberForDep = (() => {
        const a = (dataReader.getTPAppXMLNode?.()[0] as any)?.attributes ?? {};
        return (a.MatterNumber ?? '').toString().trim();
    })();
    useEffect(() => {
        console.log('[eSign] auto-fetch useEffect fired — jwt:', !!jwt, 'matterNumberForDep:', matterNumberForDep);
        fetchSessions();
    }, [jwt, matterNumberForDep]);

    // Refetch whenever the user navigates onto the Client Portal tab. The
    // Dashboard host may stay mounted across tab switches (just hidden), so
    // re-entering doesn't trigger a remount-time fetch — this effect closes
    // that gap. fetchSessions guards internally on jwt/matterNumber, so it's
    // safe to call before either has resolved.
    useEffect(() => {
        if (activeTabName === 'Client Portal') {
            console.log('[eSign] tab → Client Portal, refetching sessions');
            fetchSessions();
        }
    }, [activeTabName]);

    // Refresh the list whenever the tab becomes visible again. Handles the
    // case where the user creates a session, switches away, comes back, and
    // expects to see the latest state without manually clicking refresh.
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') fetchSessions();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [jwt, matterNumberForDep]);

    useEffect(() => {
        if (!showModal) {
            setFullscreen(false);
            // Refresh sessions list when modal closes (in case one was just created)
            fetchSessions();
            return;
        }

        // Suppress dev runtime error overlay while the cross-origin iframe is mounted
        let restoreOverlay: (() => void) | null = null;
        try {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const errorOverlay = require('react-error-overlay');
            errorOverlay.stopReportingRuntimeErrors?.();
            restoreOverlay = () => errorOverlay.startReportingRuntimeErrors?.({ onError: () => { /* no-op */ } });
        } catch {
            // ignore — overlay package may not be present in production
        }

        const handleMessage = (event: MessageEvent) => {
            if (event.data?.type === 'esignReady') {
                const data = buildInitialData(dataReader, mydriveCtx);
                // Surface the file's responsible-lawyer LSUCID so the eSign
                // service can resolve FirmEmail server-side (no UV JSON
                // endpoint exists that returns it; eSign mirrors the SQL
                // ClientConnect already runs in MobileServer/postMark.ts).
                const rootAttrs = (dataReader.getTPAppXMLNode?.()[0] as any)?.attributes ?? {};
                const responsibleLawyerLSUCID = (rootAttrs.UserID ?? '').toString().trim();
                if (responsibleLawyerLSUCID) {
                    (data as any).responsibleLawyerLSUCID = responsibleLawyerLSUCID;
                }
                iframeRef.current?.contentWindow?.postMessage({ type: 'esignInit', data }, '*');
            } else if (event.data?.type === 'esignFullscreen') {
                setFullscreen(!!event.data.value);
            } else if (event.data?.type === 'esignSessionCreated') {
                console.log('eSigning session created:', event.data.taskId);
                // Iframe navigated internally — keep modal open so user can continue.
                // Refresh the parent's session list immediately so the new card
                // appears without waiting for the modal to close.
                fetchSessions();
            } else if (event.data?.type === 'esignSubmitted') {
                console.log('eSigning session submitted:', event.data.session);
                setShowModal(false);
            } else if (event.data?.type === 'esignClose') {
                setShowModal(false);
                setEditingSessionId(null);
                setRescheduling(false);
                setFinishSetupUrl(null);
            } else if (event.data?.type === 'mydriveRequest') {
                handleMyDriveRequest(event.data, iframeRef.current?.contentWindow);
            }
        };

        // While the eSign iframe is mounted, swallow ALL runtime errors and
        // unhandled rejections so they don't surface as red overlays in Realti.
        // Trade-off: real Realti errors raised during this window also vanish —
        // acceptable because the iframe is short-lived and the eSign app has
        // its own error reporting. Errors are still logged to the console.
        const handleError = (event: ErrorEvent) => {
            console.debug('[eSign suppress] error:', event.message, event.filename);
            event.preventDefault();
            event.stopImmediatePropagation();
            return true;
        };

        const handleRejection = (event: PromiseRejectionEvent) => {
            console.debug('[eSign suppress] rejection:', event.reason);
            event.preventDefault();
            event.stopImmediatePropagation();
        };

        // Forward external file drags onto the parent window into the eSign
        // iframe. Drops onto the iframe land on Realti's window (the iframe
        // never sees them); without preventDefault the browser opens the
        // file in a new tab. Depth counter avoids flicker when the cursor
        // crosses child-element boundaries during a single drag gesture.
        let dragDepth = 0;
        const handleDragOver = (e: DragEvent) => {
            if (!iframeRef.current?.contentWindow) return;
            e.preventDefault();
        };
        const handleDragEnter = (e: DragEvent) => {
            const target = iframeRef.current?.contentWindow;
            if (!target) return;
            e.preventDefault();
            dragDepth += 1;
            if (dragDepth === 1) target.postMessage({ type: 'esignDragEnter' }, '*');
        };
        const handleDragLeave = (e: DragEvent) => {
            const target = iframeRef.current?.contentWindow;
            if (!target) return;
            e.preventDefault();
            dragDepth = Math.max(0, dragDepth - 1);
            if (dragDepth === 0) target.postMessage({ type: 'esignDragLeave' }, '*');
        };
        const handleDrop = async (e: DragEvent) => {
            const target = iframeRef.current?.contentWindow;
            if (!target) return;
            e.preventDefault();
            dragDepth = 0;
            const files = Array.from(e.dataTransfer?.files || []);
            if (!files.length) return;
            const payload = await Promise.all(files.map(async (f) => ({
                name: f.name,
                type: f.type || 'application/pdf',
                buffer: await f.arrayBuffer(),
            })));
            target.postMessage({ type: 'esignFilesDropped', files: payload }, '*');
        };

        window.addEventListener('message', handleMessage);
        window.addEventListener('error', handleError, true);
        window.addEventListener('unhandledrejection', handleRejection, true);
        window.addEventListener('dragover', handleDragOver);
        window.addEventListener('dragenter', handleDragEnter);
        window.addEventListener('dragleave', handleDragLeave);
        window.addEventListener('drop', handleDrop);
        return () => {
            window.removeEventListener('message', handleMessage);
            window.removeEventListener('error', handleError, true);
            window.removeEventListener('unhandledrejection', handleRejection, true);
            window.removeEventListener('dragover', handleDragOver);
            window.removeEventListener('dragenter', handleDragEnter);
            window.removeEventListener('dragleave', handleDragLeave);
            window.removeEventListener('drop', handleDrop);
            restoreOverlay?.();
        };
    }, [showModal]);

    // Gate the card behind the BetaUser.json "Esign" list (firm id, province,
    // or appCode). Render nothing while the allowlist is still loading so the
    // card doesn't flash in then out.
    if (allowlistLoading) return null;
    if (!allowed) return null;

    return (
        <>
            <Card style={{ backgroundColor: 'var(--bs-primary)', color: 'white' }}>
                <Card.Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Card.Title style={{ color: '#fff', margin: 0 }}>
                        <i className="fa-solid fa-signature" style={{ marginRight: '10px', color: 'white' }}></i>
                        eSigning
                    </Card.Title>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        {refreshing && (
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                fontSize: 12, fontWeight: 500, color: '#fff', opacity: 0.9,
                            }}>
                                <i className="fa-solid fa-arrows-rotate fa-spin" style={{ fontSize: 12 }}></i>
                                Refreshing…
                            </span>
                        )}
                        <Button
                            variant="link"
                            size="sm"
                            onClick={async () => {
                                if (refreshing) return;
                                setRefreshing(true);
                                const start = Date.now();
                                try {
                                    await fetchSessions();
                                } finally {
                                    // Enforce a minimum visible spinner duration so quick fetches
                                    // still register as a deliberate refresh action.
                                    const elapsed = Date.now() - start;
                                    const minMs = 600;
                                    if (elapsed < minMs) await new Promise((r) => setTimeout(r, minMs - elapsed));
                                    setRefreshing(false);
                                }
                            }}
                            disabled={refreshing}
                            title="Refresh sessions"
                            style={{ color: '#fff', textDecoration: 'none', padding: 0, lineHeight: 1 }}
                        >
                            <i className={`fa-solid fa-arrows-rotate${refreshing ? ' fa-spin' : ''}`} style={{ fontSize: 16, color: '#fff' }}></i>
                        </Button>
                        <Button
                            variant="link"
                            size="sm"
                            onClick={openEmailLog}
                            title="View email log"
                            style={{ color: '#fff', textDecoration: 'none', padding: 0, lineHeight: 1 }}
                        >
                            <i className="fa-regular fa-envelope" style={{ fontSize: 16, color: '#fff' }}></i>
                        </Button>
                        <Button
                            variant="link"
                            size="sm"
                            onClick={() => setCollapsed((c) => !c)}
                            title={collapsed ? 'Expand' : 'Minimize'}
                            style={{ color: '#fff', textDecoration: 'none', padding: 0, lineHeight: 1 }}
                        >
                            <i className={`fa-solid ${collapsed ? 'fa-chevron-down' : 'fa-chevron-up'}`} style={{ fontSize: 16, color: '#fff' }}></i>
                        </Button>
                    </div>
                </Card.Header>

                {!collapsed && (
                <Card.Body
                    className="mb-4"
                    style={{
                        position: 'relative',
                        opacity: refreshing ? 0.55 : 1,
                        transition: 'opacity 0.2s ease',
                        pointerEvents: refreshing ? 'none' : 'auto',
                    }}
                >
                    <Row>
                        <Col>
                            <p style={{ color: '#fff' }} className="mb-3"><b>Get started with eSigning</b></p>
                            <Button variant="outline-primary" style={{ color: 'white', borderColor: 'white' }} onClick={() => { setEditingSessionId(null); setFinishSetupUrl(null); setShowModal(true); }}>
                                <i className="fa-solid fa-plus" style={{ marginRight: '8px' }}></i>
                                Create New eSigning
                            </Button>
                        </Col>

                        <Col>
                            <p style={{ color: '#fff' }}><b>Created eSigning Sessions</b></p>
                            {sessions.length === 0 ? (
                                <>
                                    <p style={{ color: '#fff' }}>Don't see your session? Click the refresh button at the top of the card.</p>
                                    <p style={{ color: '#fff' }}>There are no scheduled eSigning Sessions. Scheduled sessions and sent documents will appear here.</p>
                                </>
                            ) : (
                                <div className="d-flex flex-column gap-2">
                                    {sessions.map((s: any) => {
                                        const title = s.title || s.matter?.title || 'eSigning Session';
                                        let settings: any = s.settings;
                                        if (typeof settings === 'string') {
                                            try { settings = JSON.parse(settings); } catch { settings = {}; }
                                        }
                                        settings = settings ?? {};
                                        const isInPerson = s.task_type === 'in_person';
                                        const videoEnabled = settings.video_enabled !== false;
                                        const isVideoRemote = s.task_type === 'remote_signing' && videoEnabled;
                                        const isIndependent = !isInPerson && !isVideoRemote;
                                        const isCompleted = s.status === 'completed';
                                        // Treat tasks past initial setup but with pending re-setup
                                        // (setup_complete === false — e.g. docs edited after apply-widgets,
                                        // join_url cleared server-side) the same as never-completed setup:
                                        // hide Join/View CTAs, surface Complete Setup instead.
                                        const isAbandonedSetup = s.status === 'setup' || s.status === 'ready' || s.setup_complete === false;
                                        const sessionIcon = isCompleted
                                            ? 'fa-circle-check'
                                            : isInPerson
                                                ? 'fa-signature'
                                                : isVideoRemote
                                                    ? 'fa-video'
                                                    : 'fa-paper-plane';
                                        const ctaLabel = isCompleted
                                            ? 'View Documents'
                                            : isInPerson
                                                ? 'Start Session'
                                                : isIndependent
                                                    ? 'View Document'
                                                    : 'Join Session';
                                        const scheduledDate = s.scheduled_date ?? settings.scheduled_date;
                                        const scheduledTime = s.scheduled_time ?? settings.scheduled_time;
                                        // Reschedulable window: remote video session whose start time is
                                        // either still in the future OR within the last 30 minutes (so a
                                        // session at 10:00 AM remains reschedulable until 10:30 AM).
                                        // After that 30-minute grace, the session is considered too far
                                        // gone to cancel/reschedule from this card.
                                        const isReschedulable = (() => {
                                            if (!isVideoRemote || isCompleted) return false;
                                            if (!scheduledDate || !scheduledTime) return false;
                                            const dt = new Date(`${scheduledDate}T${scheduledTime}`);
                                            if (Number.isNaN(dt.getTime())) return false;
                                            return dt.getTime() + 30 * 60 * 1000 >= Date.now();
                                        })();
                                        const docCount =
                                            s.document_count ??
                                            s.documents?.length ??
                                            s.docs?.length ??
                                            settings.documents?.length ??
                                            settings.document_count ??
                                            0;
                                        let dateLabel = '';
                                        if (scheduledDate) {
                                            const d = new Date(scheduledDate + 'T00:00:00');
                                            dateLabel = dateToXMLDate(d);
                                            if (scheduledTime) {
                                                const [h, m] = String(scheduledTime).split(':');
                                                const hourNum = parseInt(h, 10);
                                                const ampm = hourNum >= 12 ? 'PM' : 'AM';
                                                const hr12 = ((hourNum + 11) % 12) + 1;
                                                dateLabel += ` at ${hr12}:${m ?? '00'} ${ampm}`;
                                            }
                                        }
                                        let sentLabel = '';
                                        if (s.sent_at) {
                                            const dt = new Date(s.sent_at);
                                            if (!Number.isNaN(dt.getTime())) {
                                                sentLabel = dateToXMLDate(dt);
                                            }
                                        }
                                        const signerList: any[] = Array.isArray(s.signers) ? s.signers : [];
                                        // A session can only be deleted before it has started:
                                        // never once completed or once any signer has signed, and
                                        // for scheduled video sessions only while the start time is
                                        // still in the cancel/reschedule window.
                                        const canDelete = !isCompleted
                                            && !signerList.some((sg: any) => sg.status === 'signed')
                                            && (!isVideoRemote || isReschedulable);
                                        const pillStyle: React.CSSProperties = {
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            background: 'rgba(255,255,255,0.18)',
                                            border: '1px solid rgba(255,255,255,0.35)',
                                            color: '#fff',
                                            borderRadius: 999,
                                            padding: '3px 10px',
                                            fontSize: 12,
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap',
                                        };
                                        return (
                                            <div
                                                key={s.id}
                                                className={isAbandonedSetup ? 'esign-session-card-abandoned' : ''}
                                                style={{
                                                    background: isAbandonedSetup ? '#fff3cd' : 'rgba(255,255,255,0.15)',
                                                    padding: '14px 16px',
                                                    borderRadius: 8,
                                                    color: isAbandonedSetup ? '#856404' : '#fff',
                                                    position: 'relative',
                                                    border: isAbandonedSetup ? '1px solid #ffeeba' : undefined,
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, color: '#fff' }}>
                                                        <i className={`fa-solid ${sessionIcon}`} style={{ fontSize: 16, color: '#fff' }}></i>
                                                        <span style={{ fontWeight: 700, fontSize: 15, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {title}
                                                        </span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                                        {!isCompleted && (
                                                            <Button
                                                                variant="link"
                                                                size="sm"
                                                                onClick={() => { setEditingSessionId(s.id); setShowModal(true); }}
                                                                title="Edit session"
                                                                style={{ color: '#fff', textDecoration: 'none', padding: 0, lineHeight: 1 }}
                                                            >
                                                                <i className="fa-solid fa-pencil" style={{ fontSize: 14, color: '#fff' }}></i>
                                                            </Button>
                                                        )}
                                                        {(isReschedulable || canDelete) && (
                                                            <Dropdown align="end">
                                                                <Dropdown.Toggle
                                                                    as="button"
                                                                    title="More"
                                                                    style={{
                                                                        background: 'transparent',
                                                                        border: 'none',
                                                                        color: '#fff',
                                                                        padding: 0,
                                                                        lineHeight: 1,
                                                                        cursor: 'pointer',
                                                                    }}
                                                                    bsPrefix="esign-session-more"
                                                                >
                                                                    <i className="fa-solid fa-ellipsis" style={{ fontSize: 14, color: '#fff' }}></i>
                                                                </Dropdown.Toggle>
                                                                <Dropdown.Menu>
                                                                    {isReschedulable && (
                                                                        <Dropdown.Item onClick={() => { setEditingSessionId(s.id); setRescheduling(true); setShowModal(true); }}>
                                                                            <i className="fa-solid fa-calendar-pen" style={{ marginRight: 8 }}></i>
                                                                            Reschedule Session
                                                                        </Dropdown.Item>
                                                                    )}
                                                                    {canDelete && (
                                                                        <Dropdown.Item onClick={() => deleteSession(s.id)} className="text-danger">
                                                                            <i className="fa-solid fa-trash" style={{ marginRight: 8 }}></i>
                                                                            {isReschedulable ? 'Cancel and Delete Session' : 'Delete session'}
                                                                        </Dropdown.Item>
                                                                    )}
                                                                </Dropdown.Menu>
                                                            </Dropdown>
                                                        )}
                                                    </div>
                                                </div>
                                                {isIndependent ? (
                                                    <>
                                                        <div style={{ marginTop: 8, fontSize: 13, color: '#fff', opacity: 0.95 }}>
                                                            {sentLabel ? `Sent ${sentLabel}` : 'Not sent yet'} · Independent eSigning
                                                        </div>
                                                        {signerList.length > 0 && (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                                                                {signerList.map((sg: any) => {
                                                                    const signed = sg.status === 'signed';
                                                                    const statusBg = signed ? '#d1fae5' : '#fef3c7';
                                                                    const statusBorder = signed ? '#86efac' : '#fcd34d';
                                                                    const statusFg = signed ? '#065f46' : '#92400e';
                                                                    return (
                                                                        <div key={sg.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                            <span style={{
                                                                                display: 'inline-flex',
                                                                                alignItems: 'center',
                                                                                background: statusBg,
                                                                                border: `1px solid ${statusBorder}`,
                                                                                color: statusFg,
                                                                                borderRadius: 999,
                                                                                padding: '4px 12px',
                                                                                fontSize: 13,
                                                                                fontWeight: 500,
                                                                                whiteSpace: 'nowrap',
                                                                            }}>
                                                                                {sg.name}: {signed ? 'Signed' : 'Not Signed'}
                                                                            </span>
                                                                            <Dropdown align="end">
                                                                                <Dropdown.Toggle
                                                                                    as="button"
                                                                                    title="More"
                                                                                    style={{
                                                                                        background: 'transparent',
                                                                                        border: 'none',
                                                                                        color: '#fff',
                                                                                        padding: 0,
                                                                                        lineHeight: 1,
                                                                                        cursor: 'pointer',
                                                                                    }}
                                                                                    bsPrefix="esign-signer-more"
                                                                                >
                                                                                    <i className="fa-solid fa-ellipsis" style={{ fontSize: 14, color: '#fff' }}></i>
                                                                                </Dropdown.Toggle>
                                                                                <Dropdown.Menu>
                                                                                    <Dropdown.Item
                                                                                        disabled={!sg.sign_url}
                                                                                        onClick={() => {
                                                                                            if (!sg.sign_url) return;
                                                                                            const url = /^https?:\/\//i.test(sg.sign_url)
                                                                                                ? sg.sign_url
                                                                                                : `${window.location.origin}${ESIGN_APP_BASE_URL}${sg.sign_url}`;
                                                                                            navigator.clipboard?.writeText(url);
                                                                                        }}
                                                                                    >
                                                                                        <i className="fa-solid fa-link" style={{ marginRight: 8 }}></i>
                                                                                        Copy signing link
                                                                                    </Dropdown.Item>
                                                                                </Dropdown.Menu>
                                                                            </Dropdown>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                        {isAbandonedSetup ? (
                                                            <Button
                                                                variant="outline-primary"
                                                                onClick={() => openFinishSetup(s.id)}
                                                                style={{ color: 'white', borderColor: 'white', marginTop: 12 }}
                                                            >
                                                                <i className="fa-solid fa-pen-to-square" style={{ marginRight: '8px' }}></i>
                                                                Complete Setup
                                                            </Button>
                                                        ) : (isCompleted || (signerList.length > 0 && signerList.every((sg: any) => sg.status === 'signed'))) && (
                                                            <Button
                                                                variant="outline-primary"
                                                                onClick={async () => {
                                                                    if (isCompleted) {
                                                                        openCompletedDetails(s.id);
                                                                        return;
                                                                    }
                                                                    if (!jwt) return;
                                                                    // Bearer-auth fetch → blob URL → open in new tab.
                                                                    // window.open can't carry an Authorization header.
                                                                    const res = await fetch(
                                                                        `${ESIGN_API_BASE_URL}/api/tasks/${encodeURIComponent(s.id)}/package-pdf`,
                                                                        { headers: bearerHeaders(jwt) },
                                                                    );
                                                                    if (!res.ok) {
                                                                        console.warn('[eSign] package-pdf fetch failed', res.status);
                                                                        return;
                                                                    }
                                                                    const blob = await res.blob();
                                                                    const blobUrl = URL.createObjectURL(blob);
                                                                    window.open(blobUrl, '_blank', 'noopener,noreferrer');
                                                                    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
                                                                }}
                                                                style={{ color: 'white', borderColor: 'white', marginTop: 12 }}
                                                            >
                                                                <i className={`fa-solid ${isCompleted ? 'fa-folder-open' : 'fa-arrow-up-right-from-square'}`} style={{ marginRight: '8px' }}></i>
                                                                {ctaLabel}
                                                            </Button>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        {(dateLabel || docCount > 0) && (
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                                                                {dateLabel && (
                                                                    <span style={pillStyle}>
                                                                        <i className="fa-regular fa-calendar" style={{ fontSize: 11, color: '#fff' }}></i>
                                                                        {dateLabel}
                                                                    </span>
                                                                )}
                                                                {docCount > 0 && (
                                                                    <span style={pillStyle}>
                                                                        <i className="fa-regular fa-file-lines" style={{ fontSize: 11, color: '#fff' }}></i>
                                                                        {docCount} document{docCount === 1 ? '' : 's'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                        {isAbandonedSetup ? (
                                                        <Button
                                                            variant="outline-primary"
                                                            onClick={() => openFinishSetup(s.id)}
                                                            style={{ color: 'white', borderColor: 'white', marginTop: 12 }}
                                                        >
                                                            <i className="fa-solid fa-pen-to-square" style={{ marginRight: '8px' }}></i>
                                                            Complete Setup
                                                        </Button>
                                                        ) : (
                                                        <Button
                                                            variant="outline-primary"
                                                            onClick={async () => {
                                                                if (isCompleted) {
                                                                    openCompletedDetails(s.id);
                                                                    return;
                                                                }
                                                                if (isInPerson) {
                                                                    if (!jwt) return;
                                                                    try {
                                                                        const res = await fetch(`${ESIGN_API_BASE_URL}/api/tasks/${encodeURIComponent(s.id)}/start-ceremony`, {
                                                                            method: 'POST',
                                                                            headers: { ...bearerHeaders(jwt), 'Content-Type': 'application/json' },
                                                                        });
                                                                        if (!res.ok) {
                                                                            const text = await res.text().catch(() => '');
                                                                            console.warn('[eSign] start-ceremony failed', res.status, text);
                                                                            return;
                                                                        }
                                                                        const data = await res.json();
                                                                        const url = withAccount(data.ceremony_url, sid);
                                                                        window.open(url, '_blank', 'noopener,noreferrer');
                                                                    } catch (e) {
                                                                        console.warn('[eSign] start-ceremony error', e);
                                                                    }
                                                                    return;
                                                                }
                                                                if (!s.join_url) {
                                                                    console.warn('[eSign] no join_url for session', s.id);
                                                                    return;
                                                                }
                                                                // Signer join_url is JWT-in-URL — public, no OIDC.
                                                                const url = /^https?:\/\//i.test(s.join_url) ? s.join_url : `${ESIGN_APP_BASE_URL}${s.join_url}`;
                                                                window.open(url, '_blank', 'noopener,noreferrer');
                                                            }}
                                                            disabled={!isCompleted && !isInPerson && !s.join_url}
                                                            style={{ color: 'white', borderColor: 'white', marginTop: 12 }}
                                                        >
                                                            <i className={`fa-solid ${isCompleted ? 'fa-folder-open' : 'fa-arrow-up-right-from-square'}`} style={{ marginRight: '8px' }}></i>
                                                            {ctaLabel}
                                                        </Button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
                )}
            </Card>

            <style>{`
                ${fullscreen ? `
                    /* Leave a 50px gap at the top so the Realti global nav stays
                       visible (and clickable) behind the modal. Width is full,
                       height is viewport minus the gap. The backdrop is also
                       shifted so the nav isn't dimmed. */
                    .esign-iframe-modal { max-width: 100vw !important; width: 100vw !important; margin: 50px 0 0 0 !important; height: calc(100vh - 50px) !important; }
                    .esign-iframe-modal .modal-content { height: calc(100vh - 50px) !important; max-height: calc(100vh - 50px) !important; min-height: calc(100vh - 50px) !important; border-radius: 0 !important; border: 0 !important; }
                    .modal-backdrop.show { top: 50px !important; height: calc(100vh - 50px) !important; }
                ` : `
                    .esign-iframe-modal .modal-content { height: 60vh !important; max-height: 60vh !important; min-height: 0 !important; }
                `}
                /* Bootstrap alert-warning styling for abandoned-setup session cards.
                   The inner header / pills / button all set color: #fff inline; this rule
                   overrides them to the dark-amber alert text colour for readability. */
                .esign-session-card-abandoned,
                .esign-session-card-abandoned * {
                    color: #856404 !important;
                    border-color: #ffeeba !important;
                }
                .esign-session-card-abandoned .btn.btn-outline-primary {
                    border-color: #856404 !important;
                    color: #856404 !important;
                    background: transparent !important;
                }
                .esign-session-card-abandoned .btn.btn-outline-primary:hover {
                    background: rgba(133, 100, 4, 0.08) !important;
                }
                ${showModal ? `
                    body > iframe[style*="position"][style*="z-index"],
                    body > div[id*="webpack-dev-server-client-overlay"],
                    body > div[id*="error-overlay"],
                    iframe[title="webpack-dev-server-client-overlay"],
                    iframe#webpack-dev-server-client-overlay {
                        display: none !important;
                    }
                ` : ''}
            `}</style>
            <Modal show={showModal} onHide={() => { setShowModal(false); setEditingSessionId(null); setRescheduling(false); setFinishSetupUrl(null); }} size="xl" centered enforceFocus={false} autoFocus={false} restoreFocus={false} dialogClassName="esign-iframe-modal">
                <Modal.Body style={{ padding: 0 }}>
                    {/* Wait for the OIDC bootstrap (useEsignAuth → initEsignSSO)
                        to finish before opening the iframe. Until `jwt` resolves
                        the sid→sub cache is empty, `withAccount` can't append
                        ?account=<sub>, and the iframe would land in the esign
                        SPA's AccountPicker instead of the active user's
                        partition. */}
                    {!jwt ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 360, color: '#6b7280' }}>
                            <i className="fa-solid fa-arrows-rotate fa-spin" style={{ marginRight: 8 }}></i>
                            Preparing eSigning…
                        </div>
                    ) : (
                        <iframe
                            ref={iframeRef}
                            title="eSigning Setup"
                            src={(() => {
                                if (finishSetupUrl) return finishSetupUrl;
                                // Capture the LDD RPW AppID from the surrounding file context so
                                // the esign task settings record it, which lets EsignPortal
                                // notifications deep-link back to this file's Client Portal tab.
                                const lddAppId = (dataReader.getAppId?.() ?? '').toString();
                                const params = new URLSearchParams();
                                if (editingSessionId) params.set('taskId', editingSessionId);
                                if (rescheduling) params.set('mode', 'reschedule');
                                if (lddAppId) params.set('ldd_app_id', lddAppId);
                                const path = params.toString()
                                    ? `/realti-setup?${params.toString()}`
                                    : '/realti-setup';
                                return withAccount(path, sid);
                            })()}
                            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                        />
                    )}
                </Modal.Body>
            </Modal>

            <Modal
                show={showEmailLog}
                onHide={() => setShowEmailLog(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fa-regular fa-envelope" style={{ marginRight: 10 }}></i>
                        Email Log
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }} className="esign-email-log-body">
                    <style>{`
                        .esign-email-log-body .alert::before,
                        .esign-email-log-body .alert::after {
                            content: none !important;
                            display: none !important;
                        }
                        .esign-email-log-body .alert {
                            padding-left: 12px !important;
                        }
                    `}</style>
                    {loadingEmailLog && (
                        <div style={{ textAlign: 'center', padding: 24, color: '#64748b' }}>Loading…</div>
                    )}
                    {!loadingEmailLog && emailLogError && (
                        <div className="alert alert-danger">{emailLogError}</div>
                    )}
                    {!loadingEmailLog && !emailLogError && emailEvents.length === 0 && (
                        <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>
                            No emails have been sent for this file yet.
                        </div>
                    )}
                    {!loadingEmailLog && emailEvents.length > 0 && (
                        <div className="d-flex flex-column gap-2">
                            {emailEvents.map((e: any) => {
                                const isInbound = e.direction === 'inbound';
                                const eventColor = (() => {
                                    switch (e.event_type) {
                                        case 'delivered': return { bg: '#dcfce7', border: '#86efac', fg: '#166534' };
                                        case 'bounced':
                                        case 'spamcomplaint':
                                        case 'spam': return { bg: '#fee2e2', border: '#fca5a5', fg: '#991b1b' };
                                        case 'reply': return { bg: '#dbeafe', border: '#93c5fd', fg: '#1e40af' };
                                        case 'open': return { bg: '#fef3c7', border: '#fcd34d', fg: '#92400e' };
                                        default: return { bg: '#f1f5f9', border: '#cbd5e1', fg: '#334155' };
                                    }
                                })();
                                return (
                                    <div key={e.id} className="alert alert-primary mb-0" style={{ padding: 12 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                                <i className={`fa-solid ${isInbound ? 'fa-arrow-down-to-line' : 'fa-arrow-up-from-line'}`} style={{ fontSize: 12 }}></i>
                                                <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {e.subject || '(no subject)'}
                                                </span>
                                            </div>
                                            <span style={{
                                                fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
                                                background: eventColor.bg, color: eventColor.fg,
                                                border: `1px solid ${eventColor.border}`,
                                                padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap', flexShrink: 0,
                                            }}>
                                                {e.event_type}
                                            </span>
                                        </div>
                                        <div style={{ fontSize: 12, display: 'flex', flexWrap: 'wrap', gap: 12, opacity: 0.85 }}>
                                            <span>{isInbound ? 'From' : 'To'}: <span>{(isInbound ? e.from_email : e.to_email) || '—'}</span></span>
                                            <span>{formatDateTimeYMD(e.created_at)}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            <Modal
                show={!!completedTaskDetails}
                onHide={() => setCompletedTaskDetails(null)}
                size="lg"
                centered
                scrollable
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fa-solid fa-circle-check" style={{ color: 'var(--success)', marginRight: 10 }}></i>
                        Completed Session
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: 'calc(90vh - 140px)', overflowY: 'auto' }}>
                    {loadingCompletedDetails && (
                        <div style={{ textAlign: 'center', padding: 24, color: 'var(--txt-40)' }}>Loading…</div>
                    )}
                    {!loadingCompletedDetails && completedTaskDetails?.error && (
                        <div className="alert alert-danger">{completedTaskDetails.error}</div>
                    )}
                    {!loadingCompletedDetails && completedTaskDetails?.data && (() => {
                        const data = completedTaskDetails.data;
                        const completedAt = data.completed_at ? formatDateTimeYMD(data.completed_at) : null;
                        // Fetch one document's stored PDF (bearer auth) and hand back a
                        // blob URL. The endpoint streams the file as-is from storage.
                        const fetchDocPdf = async (docId: string, download: boolean): Promise<string | null> => {
                            if (!jwt) return null;
                            const res = await fetch(
                                `${ESIGN_API_BASE_URL}/api/tasks/${encodeURIComponent(completedTaskDetails.taskId)}/documents/${encodeURIComponent(docId)}/pdf${download ? '?download=1' : ''}`,
                                { headers: bearerHeaders(jwt) },
                            );
                            if (!res.ok) {
                                console.warn('[eSign] document pdf fetch failed', docId, res.status);
                                return null;
                            }
                            return URL.createObjectURL(await res.blob());
                        };
                        const pdfFilename = (name: string) =>
                            name?.toLowerCase().endsWith('.pdf') ? name : `${name || 'document'}.pdf`;
                        // Square icon button: grid centering with line-height 0 removes
                        // both Bootstrap's uneven sm padding and the Font Awesome glyph's
                        // baseline offset, so the icon sits dead-center.
                        const iconBtnStyle: React.CSSProperties = {
                            width: 28, height: 28, padding: 0,
                            display: 'inline-grid', placeItems: 'center',
                            fontSize: 13, lineHeight: 0,
                        };
                        return (
                            <>
                                {completedAt && (
                                    <p className="text-muted mb-3">Completed on {completedAt}</p>
                                )}
                                <h6 className="text-uppercase text-muted" style={{ fontSize: 12, letterSpacing: 0.5, marginBottom: 8 }}>Documents ({data.documents?.length ?? 0})</h6>
                                <div className="d-flex flex-column gap-2 mb-4">
                                    {(data.documents ?? []).map((doc: any) => (
                                        <div key={doc.document_id} style={{ border: '1px solid var(--txt-40)', borderRadius: 8, padding: 12 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                                    <i className="fa-solid fa-file-pdf" style={{ color: 'var(--txt-pdf)', fontSize: 18 }}></i>
                                                    <span style={{ fontWeight: 600, color: 'var(--txt-10)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.name}</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                                    <span style={{
                                                        fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
                                                        background: doc.status === 'completed' ? 'var(--bg-success)' : 'var(--bg-warning)',
                                                        color: doc.status === 'completed' ? 'var(--txt-success)' : 'var(--txt-warning)',
                                                        border: `1px solid ${doc.status === 'completed' ? 'var(--success)' : 'var(--warning)'}`,
                                                        padding: '2px 8px', borderRadius: 10,
                                                    }}>{doc.status}</span>
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        title="View"
                                                        style={iconBtnStyle}
                                                        onClick={async () => {
                                                            const blobUrl = await fetchDocPdf(doc.document_id, false);
                                                            if (!blobUrl) return;
                                                            window.open(blobUrl, '_blank', 'noopener,noreferrer');
                                                            setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
                                                        }}
                                                    >
                                                        <i className="fa-solid fa-eye fa-fw" style={{ verticalAlign: 'middle' }}></i>
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        title="Download"
                                                        style={iconBtnStyle}
                                                        onClick={async () => {
                                                            const blobUrl = await fetchDocPdf(doc.document_id, true);
                                                            if (!blobUrl) return;
                                                            const a = document.createElement('a');
                                                            a.href = blobUrl;
                                                            a.download = pdfFilename(doc.name);
                                                            document.body.appendChild(a);
                                                            a.click();
                                                            a.remove();
                                                            setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
                                                        }}
                                                    >
                                                        <i className="fa-solid fa-download fa-fw" style={{ verticalAlign: 'middle' }}></i>
                                                    </Button>
                                                </div>
                                            </div>
                                            {Array.isArray(doc.signers) && doc.signers.length > 0 && (
                                                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--txt-50)' }}>
                                                    {doc.signers.map((sg: any) => (
                                                        <div key={sg.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0' }}>
                                                            <span style={{ color: 'var(--txt-30)' }}>
                                                                <i className={`fa-solid ${sg.status === 'signed' ? 'fa-check-circle' : 'fa-clock'}`} style={{ color: sg.status === 'signed' ? 'var(--success)' : 'var(--txt-40)', marginRight: 6 }}></i>
                                                                {sg.name} <span style={{ color: 'var(--txt-40)' }}>({sg.role})</span>
                                                            </span>
                                                            <span style={{ color: 'var(--txt-30)', fontSize: 12 }}>
                                                                {sg.signed_at ? formatDateTimeYMD(sg.signed_at) : 'Not signed'}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <div className="d-flex gap-2 align-items-center">
                                    <Button
                                        variant="primary"
                                        onClick={async () => {
                                            if (!jwt) return;
                                            // Inline view in a new tab. window.open can't carry an
                                            // Authorization header, so fetch with the bearer first
                                            // and surface a blob URL — the eSign service returns
                                            // Content-Disposition: inline when ?download isn't set.
                                            const res = await fetch(
                                                `${ESIGN_API_BASE_URL}/api/tasks/${encodeURIComponent(completedTaskDetails.taskId)}/package-pdf`,
                                                { headers: bearerHeaders(jwt) },
                                            );
                                            if (!res.ok) {
                                                console.warn('[eSign] package-pdf inline view failed', res.status);
                                                return;
                                            }
                                            const blob = await res.blob();
                                            const blobUrl = URL.createObjectURL(blob);
                                            window.open(blobUrl, '_blank', 'noopener,noreferrer');
                                            setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
                                        }}
                                    >
                                        <i className="fa-solid fa-file-pdf" style={{ marginRight: 6 }}></i>
                                        View Documents
                                    </Button>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="outline-primary"
                                            title="Download certificate"
                                            onClick={async () => {
                                                if (!jwt) return;
                                                const res = await fetch(
                                                    `${ESIGN_API_BASE_URL}/api/tasks/${encodeURIComponent(completedTaskDetails.taskId)}/certificate?download=1`,
                                                    { headers: bearerHeaders(jwt) },
                                                );
                                                if (!res.ok) {
                                                    console.warn('[eSign] certificate download failed', res.status);
                                                    return;
                                                }
                                                const blob = await res.blob();
                                                const blobUrl = URL.createObjectURL(blob);
                                                const a = document.createElement('a');
                                                a.href = blobUrl;
                                                a.download = `certificate-${String(completedTaskDetails.taskId).slice(0, 8)}.pdf`;
                                                document.body.appendChild(a);
                                                a.click();
                                                a.remove();
                                                setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
                                            }}
                                        >
                                            <i className="fa-solid fa-certificate"></i>
                                        </Button>
                                        <Button
                                            variant="outline-primary"
                                            title="Download package"
                                            onClick={async () => {
                                                if (!jwt) return;
                                                const res = await fetch(
                                                    `${ESIGN_API_BASE_URL}/api/tasks/${encodeURIComponent(completedTaskDetails.taskId)}/package-pdf?download=1`,
                                                    { headers: bearerHeaders(jwt) },
                                                );
                                                if (!res.ok) {
                                                    console.warn('[eSign] package-pdf download failed', res.status);
                                                    return;
                                                }
                                                const blob = await res.blob();
                                                const blobUrl = URL.createObjectURL(blob);
                                                const a = document.createElement('a');
                                                a.href = blobUrl;
                                                a.download = `package-${String(completedTaskDetails.taskId).slice(0, 8)}.pdf`;
                                                document.body.appendChild(a);
                                                a.click();
                                                a.remove();
                                                setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
                                            }}
                                        >
                                            <i className="fa-solid fa-download"></i>
                                        </Button>
                                    </div>
                                </div>
                            </>
                        );
                    })()}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default ESigningCard;
